import React, { useState, useEffect } from 'react';
import {
    Accordion, AccordionSummary, AccordionDetails,
    Typography, Box, TextField, Button, Autocomplete,
    FormControlLabel, Checkbox, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import useMediaQuery from '@mui/material/useMediaQuery';
import { tokens } from '../../../theme';
import v2Api from '../../../utils/v2Api';
import ErrorSnackbar from '../../../components/ErrorSnackbar';
import dayjs from 'dayjs';

export default function AdminActionAccordionV2({
    courseId, students, userRole, v2CourseTests, studentsStartEndDateMap,
    setBackdropOpen, setSuccessAlertOpen, setErrorAlertOpen,
    setSuccessAlertMessage, setErrorAlertMessage, onRefresh,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const matches = useMediaQuery('(min-width:90rem)');

    const [testOptions, setTestOptions] = useState([]); // flat list: folders (disabled) + tests (selectable)
    const [selectedTest, setSelectedTest] = useState(null);
    const [title, setTitle] = useState('');
    const [instructions, setInstructions] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [showOnHomework, setShowOnHomework] = useState(true);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);

    // Fetch folder tree + tests, flatten into a single hierarchy list
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [catRes, testsRes] = await Promise.all([
                    v2Api.get('/test-categories', { tree: true }),
                    v2Api.get('/tests', { limit: 500 }),
                ]);
                const folders = catRes.data.data || [];
                const tests = testsRes.data.data || [];
                const assignedIds = new Set((v2CourseTests || []).map(ct => ct.test_id));

                // Group tests by category_id
                const testsByCat = {};
                tests.forEach(t => {
                    const catId = t.category_id || t.v2_test_category?.id || null;
                    if (!testsByCat[catId]) testsByCat[catId] = [];
                    if (!assignedIds.has(t.id)) testsByCat[catId].push(t);
                });

                // Flatten: folder tree with tests nested under each folder
                const flat = [];
                function walk(items, depth) {
                    for (const item of items) {
                        const indent = depth > 0 ? '  '.repeat(depth - 1) + '└ ' : '';
                        flat.push({ id: `folder-${item.id}`, label: indent + item.name, depth, isFolder: true });
                        // Tests in this folder
                        const folderTests = testsByCat[item.id] || [];
                        for (const t of folderTests) {
                            const testIndent = '  '.repeat(depth) + '  ';
                            flat.push({ id: t.id, label: testIndent + t.title, name: t.title, depth: depth + 1, isFolder: false, test: t });
                        }
                        if (item.children?.length > 0) walk(item.children, depth + 1);
                    }
                }
                walk(folders, 0);

                // Root-level tests (no folder)
                const rootTests = testsByCat[null] || testsByCat[undefined] || [];
                for (const t of rootTests) {
                    flat.push({ id: t.id, label: t.title, name: t.title, depth: 0, isFolder: false, test: t });
                }

                setTestOptions(flat);
            } catch (err) {
                console.error('Failed to fetch test options:', err);
            }
        };
        fetchData();
    }, [v2CourseTests]);

    // Filter current students
    const currentStudents = (students || []).filter(s => {
        const dates = studentsStartEndDateMap?.[s.id];
        if (!dates) return true;
        return !dayjs().isAfter(dayjs(dates.end_date)) && !dayjs().isBefore(dayjs(dates.start_date));
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTest || !dueDate) { setErrorSnackbarOpen(true); return; }

        setBackdropOpen(true);
        try {
            const studentIds = selectedStudents.length > 0
                ? selectedStudents.map(s => s.id)
                : [];

            await v2Api.post(`/courses/${courseId}/tests/${selectedTest.id}`, {
                title,
                content: instructions,
                due_date: dueDate,
                posted_by: localStorage.getItem('userName'),
                show_on_homework: showOnHomework,
                student_ids: studentIds,
            });

            setBackdropOpen(false);
            setSuccessAlertMessage('Test assigned successfully!');
            setSuccessAlertOpen(true);
            // Reset form
            setSelectedTest(null);
            setTitle('');
            setInstructions('');
            setDueDate(null);
            setSelectedStudents([]);
            setShowOnHomework(true);
            if (onRefresh) onRefresh();
        } catch (err) {
            setBackdropOpen(false);
            setErrorAlertMessage(err.response?.data?.message || 'Failed to assign test');
            setErrorAlertOpen(true);
        }
    };

    return (
        <div style={{ width: '100%', marginBottom: '1rem' }}>
            <Accordion sx={{ bgcolor: colors.primary[750] }} component="div">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Assign Test</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            '& > :not(style)': { m: 1, width: '25ch' },
                            display: 'flex',
                            flexDirection: 'column',
                            mr: '1rem',
                        }}
                        noValidate
                        autoComplete="off"
                    >
                        {/* Test selection — folder hierarchy autocomplete */}
                        <Autocomplete
                            sx={{ minWidth: '100%' }}
                            options={testOptions}
                            value={selectedTest}
                            onChange={(_, val) => {
                                if (val?.isFolder) return; // shouldn't happen due to getOptionDisabled
                                setSelectedTest(val);
                                if (val?.name) setTitle(val.name);
                            }}
                            getOptionLabel={(opt) => opt.name || opt.label || ''}
                            getOptionDisabled={(opt) => opt.isFolder}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            filterOptions={(options, { inputValue }) => {
                                if (!inputValue) return options;
                                const lower = inputValue.toLowerCase();
                                // Include matching tests + their ancestor folders
                                const includeIndices = new Set();
                                options.forEach((o, i) => {
                                    if (!o.isFolder && o.name?.toLowerCase().includes(lower)) {
                                        includeIndices.add(i);
                                        // Walk back to include parent folders
                                        let targetDepth = o.depth - 1;
                                        for (let j = i - 1; j >= 0 && targetDepth >= 0; j--) {
                                            if (options[j].isFolder && options[j].depth === targetDepth) {
                                                includeIndices.add(j);
                                                targetDepth--;
                                            }
                                        }
                                    }
                                });
                                return options.filter((_, i) => includeIndices.has(i));
                            }}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.id} style={opt.depth === 0 && opt.isFolder ? { borderTop: `1px solid ${colors.grey[700]}` } : {}}>
                                    <Typography sx={{
                                        whiteSpace: 'pre',
                                        fontWeight: opt.isFolder ? 700 : 400,
                                        color: opt.isFolder ? 'text.secondary' : 'text.primary',
                                        fontSize: opt.isFolder ? '0.85rem' : '0.95rem',
                                    }}>
                                        {opt.label}
                                    </Typography>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Select a test" />
                            )}
                            ListboxProps={{ sx: { maxHeight: 300 } }}
                        />

                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateTimePicker
                                label="Due Date"
                                value={dueDate}
                                onChange={setDueDate}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        backgroundColor: colors.primary[600],
                                        width: '16rem',
                                        ...!matches && { width: '100%' },
                                    },
                                }}
                            />
                        </LocalizationProvider>

                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1rem', minWidth: '100%' }}>
                            <TextField
                                label="Title"
                                variant="filled"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                sx={{
                                    flex: 1, maxWidth: '50%',
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ':focus': { backgroundColor: 'white' },
                                    },
                                }}
                            />
                            <TextField
                                label="Author"
                                variant="filled"
                                defaultValue={localStorage.getItem('userName')}
                                inputProps={{ readOnly: userRole !== 'admin' }}
                                sx={{
                                    flex: 1, maxWidth: '50%',
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[600],
                                        ':focus': { backgroundColor: 'white' },
                                    },
                                }}
                            />
                        </Box>

                        {userRole === 'admin' && (
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1rem', minWidth: '100%' }}>
                                <Autocomplete
                                    multiple
                                    limitTags={4}
                                    options={currentStudents}
                                    getOptionLabel={(s) => `${s.first_name} ${s.last_name}`}
                                    getOptionKey={(s) => s.id}
                                    filterSelectedOptions
                                    value={selectedStudents}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Test Takers: If no one is selected, the test will be assigned to all students"
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                    onChange={(_, val) => setSelectedStudents(val)}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                            </Box>
                        )}

                        <TextField
                            label="Instructions"
                            variant="filled"
                            fullWidth
                            multiline
                            rows={10}
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            sx={{
                                minWidth: '100%',
                                borderRadius: '5px',
                                '& .MuiFilledInput-root': {
                                    backgroundColor: colors.primary[600],
                                    ':focus': { backgroundColor: 'white' },
                                },
                            }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showOnHomework}
                                    onChange={(e) => setShowOnHomework(e.target.checked)}
                                    sx={{ color: colors.blueAccent[400], '&.Mui-checked': { color: colors.blueAccent[400] } }}
                                />
                            }
                            label="Insert as homework"
                            sx={{ minWidth: '100%' }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            style={{
                                backgroundColor: colors.primary[300],
                                color: 'white',
                                float: 'right',
                                marginTop: '0.5rem',
                                marginBottom: '1rem',
                                width: '10rem',
                            }}
                        >
                            Create
                        </Button>
                    </Box>
                </AccordionDetails>
                <ErrorSnackbar errorMsg="Please select test and set due date!" open={errorSnackbarOpen} setOpen={setErrorSnackbarOpen} />
            </Accordion>
        </div>
    );
}
